import { create } from 'zustand';
import { KeysType, getAbTestData } from '@/common/abTest';
import { KSwitchKeyType, getKSwitch } from '@/common/kswitch';
import { isIOS, localStorage } from '@kibt/krn-utils';
import { versionCompare } from '@kibt/web-utils/version';
import { appVersion } from '@/common/device-info';
import { hashCode } from '@/common/utils/hashCode';
import { reportCatchError } from '@/utils/reportCatchError';

export interface IAbState {
    // ab是否在加载中
    isAbLoading: boolean;
    // 保存加载的结果
    setAbResult: (result: Map<string, any>) => void;
    // ab加载结果的对象key，没有泛型约束
    [key: string]: any;
}

// 全局的AB Store
export const useAbStore = create<IAbState>(set => ({
    // 是否ab获取中
    isAbLoading: true,
    setAbResult: result => set(Object.assign({}, { isAbLoading: false }, result)),
}));

// 初始化全局的ab
function initGlobalAb() {
    // ab处理器，通过AB接口获取AB信息，将ab返回的结果转换成业务需要的对象
    const abApiHandlers: Array<{
        keys: KeysType;
        handler: (res: {
            [key: string]: {
                //
                value: any;
                experimentName?: string;
                groupName?: string;
            };
        }) => Record<string, any>;
    }> = [];

    abApiHandlers.push({
        keys: {
            if_krn_shop_search_fe: 'bool',
        },
        handler: res => {
            return {
                if_krn_shop_search_fe: res.if_krn_shop_search_fe?.value || false,
            };
        },
    });

    abApiHandlers.push({
        keys: {
            evaluation_of_production: 'bool', // 商品评价生产促进
            evaluation_display: 'bool', // 商详页评价楼层展示优化
            buyer_gallery: 'bool', // 商详页买家秀楼层
        },
        handler: res => {
            return {
                evaluation_of_production: res.evaluation_of_production?.value || false,
                evaluation_display: res.evaluation_display?.value || false,
                buyer_gallery: res.buyer_gallery?.value || false,
            };
        },
    });

    abApiHandlers.push({
        keys: {
            if_product_detail_addon: 'bool', // 商详页是否支持凑单楼层
            if_product_addon_spec_modifiabe: 'bool', // 商详页凑单楼层规格筛选半屏页可修改
        },
        handler: res => {
            return {
                if_product_detail_addon: res.if_product_detail_addon?.value || false,
                if_product_addon_spec_modifiabe: res.if_product_addon_spec_modifiabe?.value || false,
            };
        },
    });

    // 是否跳转到 WebPack 打包的 Bundle
    // 2024.08.25 暂时只用地址页
    abApiHandlers.push({
        keys: {
            if_krn_webpack: 'bool',
        },
        handler: res => res,
    });

    //pix-适配高屏PIX支付弹窗
    abApiHandlers.push({
        keys: {
            Eco_NewPix_lanch_bank_app: 'string',
        },
        handler: res => res,
    });

    // 提单页治理的 AB 实验
    abApiHandlers.push({
        keys: {
            shop_c_krn_inp_perf_switch: 'bool', // 是否开启跳提单页性能优化
        },
        handler: res => res,
    });

    // 客户端磁盘清理实验
    abApiHandlers.push({
        keys: {
            low_disk_clean_config: 'string',
        },
        handler: res => res,
    });

    // 实验链接：https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/10620/group?docType=12
    abApiHandlers.push({
        keys: {
            cart_add: 'bool',
            cart_shipping_fee: 'bool',
        },
        handler: res => res,
    });

    // AB实验 https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/10174/group?docType=12
    abApiHandlers.push({
        keys: {
            shipping_coupon_text: 'bool',
        },
        handler: res => {
            return res;
        },
    });

    // AB实验 https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/11040/group?docType=12
    abApiHandlers.push({
        keys: {
            kwaishop_search_opt: 'bool',
        },
        handler: res => {
            return res;
        },
    });

    // AB实验 https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/10590/group?docType=12
    abApiHandlers.push({
        keys: {
            shopcategory: 'bool',
        },
        handler: res => {
            return res;
        },
    });

    // AB实验 https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/10621/group?docType=12
    abApiHandlers.push({
        keys: {
            address_CEP_opt: 'bool',
        },
        handler: res => {
            return res;
        },
    });

    // 二次引导支付
    abApiHandlers.push({
        keys: {
            Order_entry_education: 'bool', // 支付结果页的订单入口教育+订单详情页的订单入口教育
            Countdown: 'bool', // 订单列表页的倒计时+订单详情页的倒计时
        },
        handler: res => {
            return res;
        },
    });

    // 地址页面相关ab实验
    abApiHandlers.push({
        keys: {
            field_display_on_hit_editing_page: 'bool',
            address_locating: 'bool', // 是否支持定位
            interactive_optimization: 'bool', // 是否支持交互优化
        },
        handler: res => {
            return res;
        },
    });

    // https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/9559/group?docType=12
    abApiHandlers.push({
        keys: {
            is_cart_bottom_add_on: 'bool', // 购物车凑单条吸底
            is_cart_three_tab: 'bool', // 购物车顶部多tab
            is_new_cart_ui_2405: 'bool', // 购物车利益点迭代
        },
        handler: res => {
            return res;
        },
    });

    // 购物车商品优惠表达实验 https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/9583/group?docType=12&isShared=true
    abApiHandlers.push({
        keys: {
            if_price_discount_opt: 'bool', // 是否命中购物车商品优惠表达实验
        },
        handler: res => {
            return res;
        },
    });

    // P0埋点修复实验
    abApiHandlers.push({
        keys: {
            if_hit_new_p0_log: 'bool', // 是否命中P0埋点修复实验
        },
        handler: res => {
            return res;
        },
    });

    // 提单页V2.0实验ab
    abApiHandlers.push({
        keys: { if_hit_orderpage_v2: 'bool' },
        handler: res => res,
    });

    // 购物车屏蔽实验
    abApiHandlers.push({
        keys: { is_live_shop_cart_product_list: 'bool', is_live_shop_cart_upper_right_corner: 'bool' },
        handler: res => res,
    });

    // 删除划线价实验
    abApiHandlers.push({
        keys: { if_crossed_price_without: 'bool' },
        handler: res => res,
    });

    // 商详页店铺入口是否展示旋转动画
    abApiHandlers.push({
        keys: { liveproduct2store: 'bool' },
        handler: res => res,
    });

    /**
     * 提单页动态运费券商详页利益点透出
     * https://docs.corp.kuaishou.com/d/home/fcABDMOM4-NicB_92JHFF2mJX
     */
    abApiHandlers.push({
        keys: { if_product_details_dynamic_free_shipping_coupon: 'bool' },
        handler: res => res,
    });

    /**
     * 客态P页接入纪元
     * https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/10603/group?docType=12
     */
    abApiHandlers.push({
        keys: { ecomm_profile_to_jiyuan: 'bool' },
        handler: res => res,
    });

    /**
     * 商城迁移KRN
     * https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/11164/group?docType=12
     */
    abApiHandlers.push({
        keys: { kisb_kwaishop_hit_krn: 'bool' },
        handler: res => res,
    });

    abApiHandlers.push({
        keys: { if_shelf_add_cart: 'bool' },
        handler: res => res,
    });

    /**
     * 文案优化abtask
     * https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/12666/group?docType=12
     */
    abApiHandlers.push({
        keys: { line_optimize: 'bool' },
        handler: res => res,
    });

    // kswitch to abTest
    abApiHandlers.push({
        keys: {
            if_specification_picture_opt: 'bool',
            single_spec_show: 'int',
            CEP_position: 'bool',
            address_experience_2: 'bool',
            save_button_interaction: 'bool',
            product_detail_doublepage_buy_button_interaction: 'bool',
            if_installment_amount_first: 'bool',
            add_on_coupon_type: 'int',
            add_on_btn_type: 'int',
            product_detail_bottom_benefits: 'string',
            if_detail_product_list_show_flash_sale: 'bool',
            show_price_used_coupon_style: 'bool',
            product_coupon_priority: 'bool',
            coupon_use_prompt: 'bool',
            cart_make_up_order_v2: 'bool',
            cart_check_out: 'bool',
            interactive_optimization: 'bool',
            address_locating: 'bool',
            email_opt: 'bool',
            if_order_confirm_discounts: 'bool',
        },
        handler: res => res,
    });

    const buildHandler = handlers => res => {
        if (!res) return {};

        return handlers.reduce((pre, curr) => {
            return Object.assign(pre, curr.handler(res));
        }, {});
    };

    // 合并ab结果的方法
    const doHandleAbRes = buildHandler(abApiHandlers);

    const getAbInfo = async () => {
        const startTime = Date.now();

        let abRes = {};
        const res = {};

        try {
            // 合并ab keys
            const keys = abApiHandlers.reduce((pre, curr) => {
                return Object.assign(pre, curr.keys);
            }, {});

            // 接口获取并缓存
            const [apiAbRes] = await Promise.all([getAbTestData(keys)]);
            // console.log(`[FirstScreen Ab] api ab result: `, apiAbRes);
            abRes = apiAbRes;
            // 转换结果
            abRes = doHandleAbRes(apiAbRes);
        } catch (err) {
            console.log('[FirstScreen Ab] ab error: ', err);
            reportCatchError(err, {
                position: 'useAbStore-getAbInfo',
            });
        }

        console.log(`[FirstScreen Ab] load ab cost time: ${Date.now() - startTime}ms`);
        // 设置cache，标记状态
        const state = {
            // 业务转换后的key,value对
            ...abRes,
            // 原始的ab结果
            originApiAbInfo: res,
            isAbLoading: false,
        };
        return state;
    };

    return getAbInfo();
}

export const abPromise = initGlobalAb().catch(e => {
    reportCatchError(e, {
        position: 'useAbStore-abPromise',
    });
});

abPromise.then(res => {
    useAbStore.getState().setAbResult(res);
});
